import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { useGetFundCasTemplates } from "@vega-api/generated/cas-settings-v1/cas-settings-v1";
import { type AssetClass } from "@vega-api/generated/types/assetClass";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { InformativeModal } from "@vega-core/components/Modals/InformativeModal/InformativeModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { Switch } from "@vega-core/components/Switch/Switch";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

import { FundLogos } from "../../../../components/AssetClassLogos/AssetClassImages";

import { useCasVisibility } from "./hooks/useCasVisibility";
import { CasVisibilityConfirmationModal } from "./CasVisibilityConfirmationModal";

type EditCASVisibilityModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId?: string;
  fundName: string;
  assetClass?: AssetClass;
};

type CASVisibilityFormValues = {
  isVisible: boolean;
  templateKey: string | undefined;
};

export const EditCASVisibilityModal = ({
  open,
  onOpenChange,
  fundId,
  fundName,
  assetClass,
}: EditCASVisibilityModalProps) => {
  const {
    isActive,
    currentTemplateKey,
    updateVisibility,
    isUpdating,
    isLoadingSettings,
  } = useCasVisibility(fundId);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const showSuccess = useBoolean(false);

  const { data: casTemplatesData, isLoading: isLoadingTemplates } =
    useGetFundCasTemplates();

  const templateOptions: OptionType[] = !casTemplatesData?.data
    ? []
    : casTemplatesData.data.map((template) => ({
        label: template.templateName,
        value: template.templateKey,
      }));

  const formik = useFormik<CASVisibilityFormValues>({
    initialValues: {
      isVisible: isActive,
      templateKey: currentTemplateKey,
    },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      isVisible: Yup.boolean().required(),
      templateKey: Yup.string().when("isVisible", {
        is: true,
        then: (schema) => schema.required(m.cas_validation_template_required()),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    onSubmit: () => {
      setShowConfirmation(true);
    },
  });

  const handleConfirmSave = () => {
    updateVisibility(formik.values.isVisible, formik.values.templateKey);
    setShowConfirmation(false);
    showSuccess.setTrue();
  };

  const handleCancel = () => {
    formik.resetForm();
    onOpenChange(false);
  };

  const isSaveDisabled =
    isLoadingSettings || !formik.dirty || !formik.isValid || isUpdating;

  return (
    <>
      <ActionaryModal
        ModalButtonsGroupProps={{
          mainAction: formik.handleSubmit,
          mainActionText: m.common_save(),
          secondaryAction: handleCancel,
          secondaryActionText: m.common_cancel(),
          disabled: isSaveDisabled,
        }}
        description={m.cas_edit_fund_visibility_description()}
        fluidHeight
        open={Boolean(open && !showConfirmation && !showSuccess.value)}
        setOpen={(newOpen) => {
          if (!newOpen) {
            formik.resetForm();
          }
          onOpenChange(newOpen);
        }}
        title={m.cas_edit_fund_visibility_title()}
      >
        <div className="flex flex-col gap-6 w-full sm:w-96 md:w-md min-w-80 mx-auto">
          <div className="flex items-center gap-3 p-4 border rounded-default">
            <FundLogos
              assetClass={assetClass}
              isLoading={!fundName && !fundId}
            />
            <div className="flex flex-col items-start">
              <h3 className="body-base-regular text-text/primary">
                {fundName || fundId}
              </h3>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-col items-start">
              <span className="body-base-regular text-text/primary">
                {m.cas_show_capital_account_statement()}
              </span>
            </div>
            <Switch
              checked={formik.values.isVisible}
              disabled={isLoadingSettings}
              id="cas-visibility-switch"
              onChange={(checked) => formik.setFieldValue("isVisible", checked)}
            />
          </div>

          {!formik.values.isVisible ? (
            <Notice
              content={m.cas_warning_hide_immediately()}
              type={NoticeType.WARNING}
            />
          ) : null}

          <ThemedDropdown
            disabled={
              isLoadingSettings ||
              isLoadingTemplates ||
              !formik.values.isVisible ||
              templateOptions.length === 0
            }
            isClearable={false}
            isLoading={isLoadingSettings || isLoadingTemplates}
            name="cas-template-dropdown"
            onChange={(option) =>
              formik.setFieldValue("templateKey", option?.value)
            }
            options={templateOptions}
            placeholder={m.cas_template_placeholder()}
            value={formik.values.templateKey || undefined}
          />
        </div>
      </ActionaryModal>

      <CasVisibilityConfirmationModal
        fundId={fundId}
        fundName={fundName}
        isLoading={isUpdating}
        isVisible={formik.values.isVisible}
        onConfirm={handleConfirmSave}
        onOpenChange={setShowConfirmation}
        open={showConfirmation}
        templateKey={formik.values.templateKey}
      />

      <InformativeModal
        description={
          formik.values.isVisible
            ? m.cas_visibility_enabled_description()
            : m.cas_visibility_disabled_description()
        }
        isOpen={showSuccess.value}
        onClose={() => {
          showSuccess.setFalse();
          formik.resetForm();
          onOpenChange(false);
        }}
        primaryBtnProps={{
          children: m.btn_dismiss(),
          variant: "primary",
          onClick: () => {
            showSuccess.setFalse();
            formik.resetForm();
            onOpenChange(false);
          },
        }}
        title={
          formik.values.isVisible
            ? m.cas_visibility_enabled_title()
            : m.cas_visibility_disabled_title()
        }
        variant="success"
      />
    </>
  );
};
